import { IconChevronLeft16, IconChevronRight16 } from '@warp-ds/icons/react';
import { Button, Toggle } from '@warp-ds/react';
import cn from 'classnames';
import type { PrepackageDestination } from '../../types';
import type { GeoHierarchyNode } from '../utils/destinationPicker';
import { nodeHasSelectedDescendant, nodeToDestination, sortedByName } from '../utils/destinationPicker';

type DestinationPickerDrilldownViewProps = {
    isMobile?: boolean;
    country: GeoHierarchyNode;
    expandedRegion?: string;
    selectedDestinations: PrepackageDestination[];
    isDestinationSelected: (destination: PrepackageDestination) => boolean;
    isMaxedOut: boolean;
    onBack: () => void;
    onExpandedRegionChange: (region?: string) => void;
    onToggleDestination: (destination: PrepackageDestination) => void;
};

export const DestinationPickerDrilldownView = ({
    isMobile = false,
    country,
    expandedRegion,
    selectedDestinations,
    isDestinationSelected,
    isMaxedOut,
    onBack,
    onExpandedRegionChange,
    onToggleDestination,
}: DestinationPickerDrilldownViewProps) => {
    const countryDestination = nodeToDestination(country, country.displayName || country.name);
    const countryName = country.displayName || country.name;
    const regions = sortedByName(country.children ?? []);
    const destinationCount = regions.reduce((total, region) => total + Math.max(region.children?.length ?? 0, 1), 0);

    const renderToggle = (destination: PrepackageDestination, label: React.ReactNode, indeterminate = false) => {
        const checked = isDestinationSelected(destination);

        return (
            <Toggle
                type="checkbox"
                label={label}
                checked={checked}
                disabled={isMaxedOut && !checked}
                onChange={() => onToggleDestination(destination)}
                className="py-6"
                indeterminate={indeterminate}
            />
        );
    };

    return (
        <div>
            <div className={cn('flex items-center gap-8 border-b s-border', { 'py-10 px-0': isMobile, 'px-16 py-12': !isMobile })}>
                <Button
                    type="button"
                    utility
                    quiet
                    small
                    onClick={onBack}
                    aria-label="Tilbake"
                    className={cn('flex gap-8 items-center', { 'px-0': isMobile })}>
                    <IconChevronLeft16 aria-hidden />
                    <span className="font-bold text-m">Alle destinasjoner</span>
                </Button>
            </div>
            <div className={cn({ 'max-h-[360px] overflow-y-auto px-16 py-8': !isMobile, 'py-8': isMobile })}>
                <div className={cn({ 'rounded-8 s-bg-subtle px-12 py-8': !isMobile, 'border-b s-border-subtle px-16 py-8': isMobile })}>
                    {renderToggle(
                        countryDestination,
                        <span>
                            <span className="block font-bold">{country.name} (alle)</span>
                            <span className="block text-s s-text-subtle">{destinationCount} reisemål</span>
                        </span>,
                        nodeHasSelectedDescendant({ node: country, country: countryName, selected: selectedDestinations }),
                    )}
                </div>
                <ul className={cn('list-none p-0', { 'mt-8': !isMobile })}>
                    {regions.map((region) => {
                        const regionDestination = nodeToDestination(region, countryName);
                        const destinations = sortedByName(region.children ?? []);
                        const isExpanded = expandedRegion === region.displayName;
                        const regionIsIndeterminate = nodeHasSelectedDescendant({
                            node: region,
                            country: countryName,
                            selected: selectedDestinations,
                        });

                        if (destinations.length === 0) {
                            return (
                                <li key={region.displayName} className={cn('border-b s-border', { 'px-4': !isMobile, 'px-16': isMobile })}>
                                    {renderToggle(regionDestination, region.name, regionIsIndeterminate)}
                                </li>
                            );
                        }

                        return (
                            <li key={region.displayName} className={cn('border-b s-border', { 'py-4': !isMobile })}>
                                <div className={cn('flex items-center gap-8', { 'px-4': !isMobile, 'px-16 py-4': isMobile })}>
                                    {renderToggle(regionDestination, region.name, regionIsIndeterminate)}
                                    <Button
                                        type="button"
                                        utility
                                        quiet
                                        small
                                        onClick={() => onExpandedRegionChange(isExpanded ? undefined : region.displayName)}
                                        aria-expanded={isExpanded}
                                        className="flex items-center gap-4"
                                        aria-label={`${isExpanded ? 'Skjul' : 'Vis'} ${region.name}`}>
                                        <span className="mr-4 text-s s-text-subtle">{destinations.length}</span>
                                        <IconChevronRight16
                                            aria-hidden
                                            className={cn('transition-transform', { 'rotate-90': isExpanded })}
                                        />
                                    </Button>
                                </div>
                                {isExpanded && (
                                    <ul className={cn('list-none py-4', { 'pl-24 pr-4': !isMobile, 'pl-44 pr-16': isMobile })}>
                                        {destinations.map((destinationNode) => (
                                            <li key={destinationNode.displayName}>
                                                {renderToggle(nodeToDestination(destinationNode, countryName), destinationNode.name)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
